import React, { useState, useEffect, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { Crown, CheckCircle, XCircle, Clock, Mail } from "lucide-react"; // Adicionei mais ícones
import { getClients, getProducts, getCotacaoById } from "../api";
import { AuthContext } from "../AuthContext";
import { language } from "../components/Constants";


export default function ViewQuote() {
  const { user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [itens, setItens] = useState([]);

  const cotacaoIdFromUrl = searchParams.get("cotacao");

  // Mapeamento de ícones por status
  const statusIcons = {
    aprovada: <CheckCircle size={32} color="green" />,
    rejeitada: <XCircle size={32} color="red" />,
    pendente: <Clock size={32} color="orange" />,
    enviada: <Mail size={32} color="blue" />,
    // Adicione mais status e ícones conforme necessário
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [clientsData, produtosData] = await Promise.all([
          getClients(user.token),
          getProducts(user.token),
        ]);

        setClients(clientsData);
        setProdutos(produtosData);

        if (cotacaoIdFromUrl) {
          const cotacaoData = await getCotacaoById(user.token, cotacaoIdFromUrl);

          setFormData({
            cotacao_id: cotacaoData.id,
            cliente_id: cotacaoData.cliente_id,
            validade: cotacaoData.validade,
            forma_pagamento: cotacaoData.forma_pagamento,
            tipo_frete: cotacaoData.tipo_frete,
            transportadora: cotacaoData.transportadora,
            observacao: cotacaoData.observacao,
            status: cotacaoData.status,
            data_pagamento: cotacaoData.data_pagamento,
            total_valor: cotacaoData.total_valor,
            email_ok: cotacaoData.email_ok,
          });

          setItens(
            cotacaoData.itens.map((item) => ({
              produto_id: item.produto_id,
              quantidade: item.quantidade,
              preco_unitario: item.preco_unitario,
              desconto: item.desconto,
            }))
          );
        }
      } catch (error) {
        console.error("Erro ao carregar cotação:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user.token, cotacaoIdFromUrl]);

  const formaPagamento = formData.forma_pagamento === "Credito" ? "Cartão de Crédito" :
                    formData.forma_pagamento === "Debito" ? "Cartão de Débito" :
                    formData.forma_pagamento === "Transferencia" ? "Pix" :
                    formData.forma_pagamento === "Dinheiro" ? "Dinheiro" :
                    formData.forma_pagamento === "EntradaParcelas" ? "Entrada + Parcelas" : "-";

  const calcularTotal = () => {
    return itens.reduce((total, item) => {
      return total + item.quantidade * item.preco_unitario * (1 - item.desconto / 100);
    }, 0);
  };

  const calcularDescontoTotal = () => {
    return itens.reduce((total, item) => {
      return total + item.quantidade * item.preco_unitario;
    }, 0);
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="dashboard-container">
          <div className="user-card">
            <div className="spinner"></div>
            <p>Carregando cotação...</p>
          </div>
        </div>
      </div>
    );
  }

  const clienteSelecionado = clients.find((c) => c.id === Number(formData.cliente_id));

  return (
    <div className="page-container">
      <div className="dashboard-container">
        <div className="user-card">
          <h3>
            <Crown size={20} /> Visualizar Cotação n° {formData.cotacao_id}
          </h3>

          <div className="icone-status">
            {formData.status && statusIcons[formData.status.toLowerCase()] ? (
              <p style={{ 
                color: formData.email_ok === 1 ? '#28a745' : '#dc3545',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                {formData.email_ok === 1 ? (
                  <>
                    {statusIcons[formData.status.toLowerCase()]}
                    <CheckCircle size={16} fill="#28a745" color="#fff" />
                    E-mail enviado
                  </>
                ) : (
                  <>
                    {statusIcons[formData.status.toLowerCase()]}
                    <XCircle size={16} fill="#dc3545" color="#fff" />
                    E-mail não enviado
                  </>
                )}
              </p>
            ) : (
              <span style={{ marginLeft: "32px" }}></span> // Espaço reservado se não houver ícone
            )}
          </div>

          <div className="form-group">
            <label className="labels">Cliente:</label>
            <label className="labels">{clienteSelecionado.razao_social} ({clienteSelecionado.cnpj})</label>
          </div>

          <h4 style={{ color: "var(--text_color-secondary)", margin: "1.5rem 0 1rem 0" }}>
            Itens da Cotação
          </h4>

          {itens.map((item, index) => {
            const produtoSelecionado = produtos.find((p) => p.id === Number(item.produto_id));

            return (
              <div key={index} className="item-row">
                <div className="form-group item-input">
                  <label>Produto:</label>
                  <label>{produtoSelecionado.codigo} - {produtoSelecionado.referencia}</label>
                  {produtoSelecionado && (
                    <small
                      style={{
                        color: "var(--text_color-secondary)",
                        fontSize: "0.8rem",
                        fontStyle: "italic",
                      }}
                    >
                      {produtoSelecionado.descricao}
                    </small>
                  )}
                </div>

                <div className="form-group item-input">
                  <label>Qtd:</label>
                  <label>{item.quantidade.replace('.', ',')}</label>
                </div>

                <div className="form-group item-input">
                  <label>R$ Unit.:</label>
                  <label>{item.preco_unitario.replace('.', ',')}</label>
                </div>

                <div className="form-group item-input">
                  <label>Desc %</label>
                  <label>{item.desconto.replace('.', ',')}</label>
                </div>
              </div>
            );
          })}

          <div className="total-row">
            <strong style={{ marginLeft: '1rem', color: '#d9534f' }}>
              Desconto Total: R$ {(calcularDescontoTotal() - calcularTotal()).toLocaleString(language(), {minimumFractionDigits: 2, maximumFractionDigits: 2})}
            </strong><br/>
            <strong>Total: R$ {calcularTotal().toLocaleString(language(), {minimumFractionDigits: 2, maximumFractionDigits: 2})}</strong>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="labels">Validade: {new Date(formData.validade).toLocaleDateString(language()) || ""}</label>
            </div>
            <div className="form-group">
              <label className="labels">Forma de Pagamento: {formaPagamento}</label>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="labels">Tipo de Frete: {formData.tipo_frete}</label>
            </div>
            <div className="form-group">
              <label className="labels">Transportadora: {formData.transportadora}</label>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="labels">Status: {formData.status}</label>
            </div>
            <div className="form-group">
              <label className="labels">Data do Pagamento: {formData.data_pagamento && !isNaN(new Date(formData.data_pagamento)) 
                ? new Date(formData.data_pagamento).toLocaleDateString(language()) 
                : "Não informado"
              }</label>
            </div>
          </div>

          <div className="form-group">
            <label className="labels">Observações:</label>
            <textarea value={formData.observacao || ""} rows="3" disabled />
          </div>
        </div>
      </div>
    </div>
  );
}